/**
 * When to identify, and when to reset.
 *
 * Extracted from the PostHog provider as a pure decision so it can be tested
 * without a React tree, because getting it wrong is silent and expensive:
 * `posthog.reset()` starts a fresh session, mints a new distinct id and clears
 * every registered super property. Called at the wrong moment it turns one
 * anonymous person into several, cuts journeys in half at document boundaries,
 * and strips the deployment identity off everything captured afterwards.
 */

export type AnalyticsAuthStatus =
  | "unknown"
  | "authenticated"
  | "unauthenticated";

export type IdentityAction =
  | { kind: "noop" }
  | { kind: "identify"; userId: string; isNewIdentity: boolean }
  | { kind: "switch"; userId: string }
  | { kind: "reset" };

export function resolveIdentityAction(input: {
  status: AnalyticsAuthStatus;
  /** The session's user, when there is one. */
  userId: string | null;
  /** Who this provider last identified, or null if it never has. */
  identifiedUserId: string | null;
}): IdentityAction {
  // The query has not answered yet. Doing anything here acts on a guess.
  if (input.status === "unknown") return { kind: "noop" };

  if (input.status === "authenticated" && input.userId) {
    if (input.identifiedUserId && input.identifiedUserId !== input.userId) {
      return { kind: "switch", userId: input.userId };
    }

    return {
      kind: "identify",
      userId: input.userId,
      isNewIdentity: input.identifiedUserId !== input.userId,
    };
  }

  // Signed out — but only somebody who was signed IN can sign out. An anonymous
  // visitor reaching this branch is just the session query returning null, and
  // resetting there was the bug.
  if (input.identifiedUserId === null) return { kind: "noop" };

  return { kind: "reset" };
}
